import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { Button } from "@/components/ui/button";
import { fmtDateTime, flagEmoji } from "@/lib/format";

export default function Requests() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["offer-requests"], queryFn: async () => (await http.get("/offer-requests")).data });
  const rows = data?.items || [];

  const act = async (id, action) => {
    setBusy(true);
    try {
      await http.post(`/offer-requests/${id}/${action}`);
      qc.invalidateQueries({ queryKey: ["offer-requests"] });
      qc.invalidateQueries({ queryKey: ["nav-counters"] });
      qc.invalidateQueries({ queryKey: ["offer-cards"] });
      toast.success(action === "approve" ? "Access granted" : "Request rejected");
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const columns = [
    { key: "buyer", label: "Buyer", render: (r) => (
      <div><span className="font-medium text-foreground">{r.buyer_seq != null ? `#${r.buyer_seq} ` : ""}{r.buyer_name || "—"}</span>
        <span className="block text-xs text-muted-foreground">{r.buyer_email || "—"}</span></div>
    ) },
    { key: "buyer_id", label: "Buyer ID", mono: true, render: (r) => <span className="font-mono text-xs text-muted-foreground break-all">{r.buyer_id}</span> },
    { key: "offer", label: "GEO Offer", render: (r) => (
      <span className="flex items-center gap-1.5 text-foreground/90">
        {r.offer_geo && <span className="text-base leading-none">{flagEmoji(r.offer_geo)}</span>}
        {r.offer_seq != null ? `#${r.offer_seq} ` : ""}{r.offer_name || "—"}
      </span>
    ) },
    { key: "created_at", label: "Requested", sortable: true, render: (r) => fmtDateTime(r.created_at) },
    { key: "status", label: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    { key: "actions", label: "", align: "right", render: (r) => r.status === "pending" ? (
      <div className="flex justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <Button size="sm" disabled={busy} onClick={() => act(r.id, "approve")} data-testid={`approve-request-${r.id}`} className="h-7 gap-1 bg-[#22C55E] text-xs text-black hover:bg-[#16a34a]"><Check className="h-3 w-3" /> Approve</Button>
        <Button size="sm" disabled={busy} onClick={() => act(r.id, "reject")} data-testid={`reject-request-${r.id}`} className="h-7 gap-1 bg-[#EF4444] text-xs text-white hover:bg-[#dc2626]"><X className="h-3 w-3" /> Reject</Button>
      </div>
    ) : <span className="text-xs text-muted-foreground/50">—</span> },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Requests" subtitle="Buyer access requests for request-only GEO offers." />
      <DataTablePro testId="requests-table" columns={columns} rows={rows} loading={isLoading}
        searchKeys={["buyer_name", "buyer_email", "buyer_id", "offer_name"]} searchPlaceholder="Search by buyer, email, offer..."
        exportName="offer-requests"
        filters={[{ key: "status", label: "Status", multi: true, getValue: (r) => r.status,
          options: [{ value: "pending", label: "Pending" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }] }]}
        emptyText="No access requests yet." />
    </div>
  );
}
